/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import {
    IGroup,
    IGroupDelivery,
    IGroups,
    IHelp,
    IOrderDetailsViewProps,
    IOrderInformation,
    IOrderSummary,
    IPaymentMethods,
    ISalesLine
} from '@msdyn365-commerce-modules/order-management';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { getCIROrderTrackingBySalesIdAsync } from '../../../actions/DataActionExtension.g';

export const OrderInfomation: React.FC<IOrderInformation> = ({
    orderInformationProps,
    salesId,
    receiptId,
    createdDate,
    count,
    amount,
    channelReferenceId,
    channelName,
    channelAddress
}) => (
    <Node {...orderInformationProps}>
        {channelName}
        {channelAddress}
        {salesId}
        {receiptId}
        {createdDate}
        {count}
        {amount}
        {channelReferenceId}
    </Node>
);

export const SalesLine: React.FC<ISalesLine> = ({ salesLineProps, salesLine, buyAgainButton, errors }) => (
    <Node {...salesLineProps}>
        {salesLine}
        {buyAgainButton}
        {errors}
    </Node>
);

export const GroupDelivery: React.FC<IGroupDelivery> = ({ deliveryProps, heading, count }) => (
    <Node {...deliveryProps}>
        {heading}
        {count}
    </Node>
);

export interface IGroupExt extends IGroup {
    context: any,
    salesId: string,
    trackingLabel: string
}

export const Group: React.FC<IGroupExt> = ({ groupProps, delivery, address, salesLinesProps, salesLines, isCashAndCarryTransaction, context, salesId, trackingLabel }) => {
    const [trackingNumbersObj, setTrackingNumbersObj]= useState({});
    const fedexTrackUrl = 'https://www.fedex.com/apps/fedextrack/?action=track&trackingnumber=';
    
    useEffect(() => {
        const _getTrackingNumber = async() => {
            const itemIds = salesLines.map(line => {
                return line.data.salesLine.ItemId;
            });

            if (salesId && context) {
                getCIROrderTrackingBySalesIdAsync(salesId, { callerContext: {requestContext: context.request}}).then( result => {
                    const trackingObj = {};
                    result.CIROrderTrackingLineData?.map(data => {
                        if (data.ItemId && itemIds.includes(data.ItemId)) {
                            trackingObj[data.ItemId] = {
                                number: data.TrackingNumber || data.MasterTrackingNumber,
                                hasNumber: data.TrackingNumber ? true : false
                            }
                        }
                    });
                    setTrackingNumbersObj(trackingObj);
                });
            }
        }

        _getTrackingNumber();
    }, []);

    return (
        <Node {...groupProps}>
            {delivery && <GroupDelivery {...delivery} />}
            <Node className='ms-order-details__sales-lines_container'>
                {salesLines && (
                    <Node {...salesLinesProps}>
                        {salesLines.map(salesLine => (
                            <React.Fragment key={salesLine.data.salesLine.LineId}>
                                {
                                    (salesLine.data.deliveryType === 'ship'
                                        && (
                                            salesLine.data.salesLine.ItemId
                                            && trackingNumbersObj[salesLine.data.salesLine.ItemId])
                                    )
                                    ?
                                    <>
                                        {trackingNumbersObj[salesLine.data.salesLine.ItemId]?.['hasNumber']
                                        ?
                                        <>
                                            <span className='ms-order-details__tracking-info'>{trackingLabel}</span>
                                            <a className='ms-order-details__tracking-number-link' target='_blank' href={fedexTrackUrl + trackingNumbersObj[salesLine.data.salesLine.ItemId]['number']}>
                                                {trackingNumbersObj[salesLine.data.salesLine.ItemId]['number']}
                                            </a>
                                        </>
                                        : 
                                        <span className='ms-order-details__tracking-info'>{trackingNumbersObj[salesLine.data.salesLine.ItemId]['number']}</span>
                                        }
                                    </>
                                    : null
                                }
                                <SalesLine {...salesLine} />
                                {!isCashAndCarryTransaction && salesLine.salesStatus}
                            </React.Fragment>
                        ))}
                    </Node>
                )}
                {!delivery.showTimeslot && address}
                {delivery.showTimeslot && (
                    <Node {...delivery.pickupProps}>
                        {address}
                        {delivery.pickupDateTimeslot}
                    </Node>
                )}
            </Node>
        </Node>
    );
}

export interface IGroupsExt extends IGroups {
    context: any,
    salesId: string,
    trackingLabel: string
}

export const Groups: React.FC<IGroupsExt> = ({ groupsProps, groups, context, salesId, trackingLabel }) => (
    <Node {...groupsProps}>
        {groups.map((group, index) => (
            <Group key={index} {...group} context={context} salesId={salesId} trackingLabel={trackingLabel} />
        ))}
    </Node>
);

export const OrderSummary: React.FC<IOrderSummary> = ({ orderSummaryProps, heading, subtotal, shipping, tax, totalAmount, earnedPoints }) => (
    <Node {...orderSummaryProps}>
        {heading}
        {subtotal}
        {shipping}
        {tax}
        {totalAmount}
        {earnedPoints}
    </Node>
);

export const Payment: React.FC<IPaymentMethods> = ({ paymentMethodsProps, title, methods }) => (
    <Node {...paymentMethodsProps}>
        {title}
        {methods}
    </Node>
);

export const Help: React.FC<IHelp> = ({ helpProps, needHelpLabel, helpLineNumberLabel, contactNumber }) => (
    <Node {...helpProps}>
        {needHelpLabel}
        {helpLineNumberLabel}
        {contactNumber}
    </Node>
);

const OrderDetailsView: React.FC<IOrderDetailsViewProps> = ({
    moduleProps,
    viewModes,
    tableViewActions,
    table,
    heading,
    alert,
    loading,
    orderInfomation,
    orderSummary,
    payment,
    help,
    groups,
    salesOrder,
    resources
}) => {
    return (
        <Module {...moduleProps}>
            {heading}
            {alert}
            {loading}
            {orderInfomation && <OrderInfomation {...orderInfomation} />}
            {tableViewActions}
            {viewModes}
            {table}
            {groups && <Groups {...groups} context={moduleProps.moduleProps?.context} salesId={salesOrder?.SalesId || ''} trackingLabel={resources.trackingLabel} />}
            <Node className='ms-order-details__order-summary-container'>
                {orderSummary && <OrderSummary {...orderSummary} />}
                {payment && <Payment {...payment} />}
            </Node>
            {help && <Help {...help} />}
        </Module>
    );
};

export default OrderDetailsView;
